import React from 'react';
import { PageSEO } from '../components/SEO';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { TrendingUp, Target, CheckCircle, ArrowRight, BarChart3, Users, Lightbulb, MapPin } from 'lucide-react';

const QRMEducation = () => {
  const trainingLevels = [
    {
      level: "QRM Alpha",
      title: "Foundation Training",
      duration: "2-3 days",
      description: "Introduction to QRM concepts, MCT fundamentals, Value Stream Mapping basics",
      topics: [
        "Manufacturing Critical-path Time (MCT) introduction",
        "Little's Law and flow fundamentals", 
        "Value Stream Mapping methodology",
        "QRM vs Lean differences",
        "Basic system dynamics"
      ],
      targetAudience: "Plant managers, team leaders, improvement coordinators",
      prerequisites: "Basic manufacturing knowledge",
      outcome: "Understanding of QRM principles and readiness for implementation planning"
    },
    {
      level: "QRM Silver", 
      title: "Advanced Implementation",
      duration: "5-7 days",
      description: "Advanced QRM concepts, team formation, change management, company-wide application",
      topics: [
        "Advanced system dynamics and utilization management",
        "QRM team formation and leadership",
        "Change management for QRM transformation",
        "Company-wide QRM application strategies",
        "POLCA system introduction",
        "Multi-moment analysis and pain point identification"
      ],
      targetAudience: "Implementation teams, middle management, QRM champions",
      prerequisites: "QRM Alpha completion or equivalent experience",
      outcome: "Ability to lead QRM implementation and manage organizational change"
    },
    {
      level: "QRM Gold",
      title: "Expert Mastery",
      duration: "7-10 days", 
      description: "Expert-level QRM mastery, advanced finance, strategic planning, solution-focused leadership",
      topics: [
        "QRM Gold Finance methodology and ROI analysis",
        "Advanced system dynamics (Kingman's Equation)",
        "Solution-focused work and HOST leadership",
        "Strategic planning and buffering strategies",
        "Power of Six mathematical principles",
        "Operational, tactical, and strategic QRM planning"
      ],
      targetAudience: "Senior management, QRM consultants, implementation leaders",
      prerequisites: "QRM Silver completion and implementation experience",
      outcome: "Expert-level QRM knowledge and ability to lead complex transformations"
    }
  ];

  const trainingInstitutes = [
    {
      name: "University of Wisconsin-Madison",
      description: "Professor Rajan Suri's academic home and the birthplace of QRM methodology. Offers the most authoritative QRM education with research-based curriculum and university credentials.",
      specialties: ["QRM Certificate Program", "QRM Strategic Leadership", "QRM Shop Floor Mastery", "QRM Introduction", "POLCA System"],
      location: "Madison, Wisconsin, USA + Live Online",
      website: "https://interpro.wisc.edu/topic/industrial/quick-response-manufacturing/",
      keyFeatures: [
        "Professor Rajan Suri's academic home institution",
        "Research-based QRM curriculum",
        "University of Wisconsin-Madison credentials",
        "QRM Certificate with AI and digital transformation",
        "Center for Quick Responsive Manufacturing partnership",
        "Technical leadership integration",
        "Live online and in-person options"
      ],
      targetAudience: "Industry professionals seeking university-level QRM credentials and research-based knowledge",
      authority: "QRM Creator's Academic Institution",
      type: "academic",
      pricing: "$250 - $1,975 per course",
      uniqueValue: "Academic rigor + Creator's direct oversight + Research integration"
    },
    {
      name: "QRM Institute",
      description: "The only internationally recognized QRM certification program, designed by combining practitioner experience from member organizations.",
      specialties: ["QRM Bronze®", "QRM Silver®", "QRM Gold®", "QRM Platinum®"],
      location: "International",
      website: "https://qrminstitute.org/training/",
      keyFeatures: [
        "Official international certification authority",
        "Four-level progression (Bronze → Platinum)",
        "Individual coaching at Gold/Platinum levels",
        "Digital badges and certificates",
        "Practitioner-designed curriculum",
        "Member organization experience integration"
      ],
      targetAudience: "Global manufacturing professionals seeking official QRM certification",
      authority: "Official QRM Certification Authority",
      type: "certification",
      pricing: "Contact for pricing",
      uniqueValue: "International recognition + Official certification + Practitioner focus"
    },
    {
      name: "QRM Online Training", 
      description: "Direct online training from Professor Rajan Suri, the founder and creator of Quick Responsive Manufacturing methodology.",
      specialties: ["Executive QRM Overview", "Employee Training", "POLCA System (FREE)", "Direct Creator Access"],
      location: "Online (Global Access)",
      website: "https://www.qrmonlinetraining.com/",
      keyFeatures: [
        "Created personally by QRM founder Rajan Suri",
        "Online accessibility worldwide",
        "Strategic overview for executives",
        "Employee training for implementation",
        "FREE POLCA production control course",
        "Direct contact with creator available"
      ],
      targetAudience: "Executives, managers, and employees seeking authentic QRM knowledge",
      authority: "QRM Creator Direct",
      type: "founder",
      pricing: "Contact for pricing",
      uniqueValue: "Direct creator access + Online convenience + FREE POLCA course"
    },
    {
      name: "QRM Management Center Netherlands",
      description: "Dutch-language QRM training with practical, hands-on approach using games, simulations, and real-world applications.",
      specialties: ["QRM Silver® Specialist", "QRM Games", "Leadership Training", "Team Collaboration", "In-Company Programs"],
      location: "Netherlands (Tilburg, Nieuwegein, Utrecht)",
      website: "https://qrm-managementcenter.nl/qrm-opleidingen/",
      keyFeatures: [
        "Dutch-language training programs",
        "QRM Silver® specialist certification",
        "Interactive games and simulations",
        "Leadership development focus",
        "In-company customized training",
        "Practical implementation support"
      ],
      targetAudience: "Dutch/Flemish managers, consultants, and QRM facilitators",
      authority: "Local Netherlands Expertise",
      type: "regional",
      pricing: "Contact for pricing",
      uniqueValue: "Dutch language + Interactive approach + Local market expertise"
    },
    {
      name: "Quick Responsive Enterprise",
      description: "French QRM consulting and training expert, QUALIOPI certified with 100% certification success rate. Specializes in operational agility and executive-level QRM programs.",
      specialties: ["QRM Institute Bronze®", "QRM Institute Silver®", "QRM Institute Gold®", "QRM Executives® (Custom)", "Operational Agility"],
      location: "France (Rhône-Alpes region)",
      website: "https://www.quickresponse-enterprise.com/formations/",
      keyFeatures: [
        "QUALIOPI certified training organization (#82 69 13494 69)",
        "100% certification success rate (196+ people trained)",
        "French-language QRM Institute programs",
        "Custom executive programs for leadership",
        "Operational agility consulting expertise",
        "Practical case study methodology"
      ],
      targetAudience: "French-speaking executives, managers, and operational teams seeking QRM transformation",
      authority: "French QRM Excellence & Executive Focus",
      type: "executive",
      pricing: "Contact for custom pricing",
      uniqueValue: "French expertise + Executive focus + 100% success rate + Custom leadership programs"
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 py-12">
      <PageSEO path="/qrm-education" />
      <div className="container mx-auto px-6">
        <div className="max-w-6xl mx-auto">
          
          {/* Header */}
          <div className="text-center mb-16">
            <h1 className="text-4xl font-bold text-gray-900 mb-4">QRM Education & Training by Jan Pauwels</h1>
            <p className="text-xl text-gray-600 mb-2">
              <strong>"Education First"</strong> - The Foundation of Successful QRM Implementation
            </p>
            <p className="text-lg text-gray-500">
              Professional QRM training programs from Alpha to Gold level mastery
            </p>
          </div>

          {/* The Essence of QRM */}
          <div className="max-w-4xl mx-auto mb-16 px-2">
            <h2 className="text-3xl font-bold text-gray-900 mb-8" style={{ fontFamily: "'Outfit', sans-serif" }}>
              The Essence of Quick Responsive Manufacturing (QRM)
            </h2>
            <div className="space-y-6 text-gray-700 text-lg leading-relaxed">
              <p>
                At its core, Quick Responsive Manufacturing is a comprehensive, time-based enterprise strategy engineered specifically for high-mix, low-volume, and custom manufacturing environments. Unlike traditional methods that focus solely on cost-cutting, QRM targets the radical reduction of <strong>Lead Times</strong> across both the shop floor and office operations.
              </p>
              <p>
                By ruthlessly eliminating non-value-added time and 'system slack,' QRM empowers organizations to transform agility into a competitive weapon. This approach does more than just speed up production; it optimizes <strong>ROI</strong>, slashes unnecessary inventory, and allows manufacturers to meet dynamic market demands with surgical precision.
              </p>
              <p>
                Whether used as a standalone strategy or as a powerful catalyst alongside Lean and Six Sigma, the Smart QRM Navigator provides the specific time-reduction tools needed to navigate today's complex production landscape and drive sustainable <strong>EBITDA growth</strong>.
              </p>
            </div>
            <p className="text-sm italic mt-8" style={{ color: '#9CA3AF' }}>
              Based on the foundational principles of Rajan Suri, modernized for the AI-driven manufacturing era.
            </p>
            <div className="mt-10 pt-8 border-t border-gray-100 text-center">
              <p className="text-xl font-semibold text-gray-900 mb-5" style={{ fontFamily: "'Outfit', sans-serif" }}>
                Ready to eliminate system slack and transform your lead times into a competitive weapon?
              </p>
              <a href="/business-case" className="inline-flex items-center justify-center gap-2 font-bold px-8 py-4 rounded text-base shadow-sm hover:shadow-md transition-all duration-200" style={{ backgroundColor: '#EAB308', color: '#0A192F' }} data-testid="essence-cta">
                Calculate Your ROI Potential <ArrowRight className="h-5 w-5" />
              </a>
            </div>
          </div>
          <Card className="p-8 mb-12 bg-gradient-to-r from-blue-50 to-indigo-50 border-blue-200">
            <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
              <Lightbulb className="mr-3 h-6 w-6 text-blue-600" />
              Why Education Comes First
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div>
                <h3 className="text-xl font-semibold text-gray-900 mb-4">Foundation for Success</h3>
                <ul className="space-y-3 text-gray-700">
                  <li className="flex items-start">
                    <CheckCircle className="mr-2 h-5 w-5 text-green-500 mt-0.5" />
                    <span><strong>Understanding Before Action:</strong> QRM requires mindset shift from cost-thinking to time-based thinking</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="mr-2 h-5 w-5 text-green-500 mt-0.5" />
                    <span><strong>Authentic Methodology:</strong> Learn Rajan Suri's original concepts, not simplified versions</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="mr-2 h-5 w-5 text-green-500 mt-0.5" />
                    <span><strong>Team Alignment:</strong> Everyone speaks the same QRM language and principles</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="mr-2 h-5 w-5 text-green-500 mt-0.5" />
                    <span><strong>Avoid Costly Mistakes:</strong> Proper training prevents implementation failures</span>
                  </li>
                </ul>
              </div>
              
              <div>
                <h3 className="text-xl font-semibold text-gray-900 mb-4">Investment in Knowledge</h3>
                <ul className="space-y-3 text-gray-700">
                  <li className="flex items-start">
                    <Target className="mr-2 h-5 w-5 text-blue-500 mt-0.5" />
                    <span><strong>ROI on Training:</strong> Education investment pays back 10x+ through proper implementation</span>
                  </li>
                  <li className="flex items-start">
                    <Target className="mr-2 h-5 w-5 text-blue-500 mt-0.5" />
                    <span><strong>Progressive Learning:</strong> Alpha → Silver → Gold pathway builds expertise systematically</span>
                  </li>
                  <li className="flex items-start">
                    <Target className="mr-2 h-5 w-5 text-blue-500 mt-0.5" />
                    <span><strong>Competitive Advantage:</strong> Well-trained teams implement QRM 3x faster</span>
                  </li>
                  <li className="flex items-start">
                    <Target className="mr-2 h-5 w-5 text-blue-500 mt-0.5" />
                    <span><strong>Sustainable Results:</strong> Education ensures long-term QRM success</span>
                  </li>
                </ul>
              </div>
            </div>
          </Card>

          {/* Training Progression */}
          <h2 className="text-2xl font-bold text-gray-900 mb-8 text-center">QRM Training Progression</h2>
          <div className="space-y-8 mb-12">
            {trainingLevels.map((training, index) => (
              <Card key={index} className="p-8 hover:shadow-lg transition-all duration-300">
                <div className="flex items-start justify-between mb-6">
                  <div>
                    <div className="flex items-center mb-3">
                      <div className={`w-12 h-12 rounded-full flex items-center justify-center text-white font-bold text-lg mr-4 ${
                        index === 0 ? 'bg-blue-500' : index === 1 ? 'bg-purple-500' : 'bg-yellow-500'
                      }`}>
                        {index + 1}
                      </div>
                      <div>
                        <h3 className="text-2xl font-bold text-gray-900">{training.level}</h3>
                        <p className="text-lg text-gray-600">{training.title}</p>
                      </div>
                    </div>
                    <p className="text-gray-700 leading-relaxed mb-4">{training.description}</p>
                  </div>
                  <Badge className={`px-4 py-2 text-sm font-medium ${
                    index === 0 ? 'bg-blue-100 text-blue-800' : 
                    index === 1 ? 'bg-purple-100 text-purple-800' : 
                    'bg-yellow-100 text-yellow-800'
                  }`}>
                    {training.duration}
                  </Badge>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                  {/* Topics */}
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-3">Key Topics Covered:</h4>
                    <ul className="space-y-2">
                      {training.topics.map((topic, topicIndex) => (
                        <li key={topicIndex} className="flex items-start text-sm text-gray-600">
                          <div className="w-2 h-2 bg-gray-400 rounded-full mt-2 mr-3 flex-shrink-0"></div>
                          {topic}
                        </li>
                      ))}
                    </ul>
                  </div>

                  {/* Details */}
                  <div className="space-y-4">
                    <div>
                      <h4 className="font-semibold text-gray-900 mb-2">Target Audience:</h4>
                      <p className="text-sm text-gray-600">{training.targetAudience}</p>
                    </div>
                    <div>
                      <h4 className="font-semibold text-gray-900 mb-2">Prerequisites:</h4>
                      <p className="text-sm text-gray-600">{training.prerequisites}</p>
                    </div>
                    <div>
                      <h4 className="font-semibold text-gray-900 mb-2">Learning Outcome:</h4>
                      <p className="text-sm text-gray-600">{training.outcome}</p>
                    </div>
                  </div>
                </div>
              </Card>
            ))}
          </div>

          {/* Training Institutes */}
          <Card className="p-8 mb-12 bg-gradient-to-r from-green-50 to-emerald-50 border-green-200">
            <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
              <Users className="mr-3 h-6 w-6 text-green-600" />
              Recommended Training Institutes
            </h2>
            <p className="text-gray-700 mb-8 leading-relaxed">
              Professional QRM training from certified institutes with proven track records in delivering 
              authentic Rajan Suri methodology and successful implementation support.
            </p>
            
            <div className="space-y-8">
              {trainingInstitutes.map((institute, index) => (
                <Card key={index} className="p-8 bg-white hover:shadow-xl transition-all duration-300">
                  <div className="flex items-start justify-between mb-6">
                    <div>
                      <h3 className="text-2xl font-bold text-gray-900 mb-2">{institute.name}</h3>
                      <div className="flex items-center space-x-4 text-sm text-gray-600 mb-3">
                        <span className="flex items-center">
                          <MapPin className="mr-1 h-4 w-4" />
                          {institute.location}
                        </span>
                        <Badge className={`px-3 py-1 text-sm font-medium ${
                          institute.type === 'academic' ? 'bg-red-100 text-red-800 border-red-300 font-bold' :
                          institute.type === 'certification' ? 'bg-blue-100 text-blue-800' :
                          institute.type === 'founder' ? 'bg-yellow-100 text-yellow-800' :
                          institute.type === 'executive' ? 'bg-purple-100 text-purple-800 border-purple-300 font-bold' :
                          'bg-green-100 text-green-800'
                        }`}>
                          {institute.authority}
                        </Badge>
                      </div>
                      <p className="text-gray-700 leading-relaxed">{institute.description}</p>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                    {/* Key Features */}
                    <div>
                      <h4 className="font-semibold text-gray-900 mb-4">Key Features:</h4>
                      <ul className="space-y-2">
                        {institute.keyFeatures.map((feature, featureIndex) => (
                          <li key={featureIndex} className="flex items-start text-sm text-gray-600">
                            <CheckCircle className="mr-2 h-4 w-4 text-green-500 mt-0.5 flex-shrink-0" />
                            {feature}
                          </li>
                        ))}
                      </ul>
                    </div>

                    {/* Specialties & Details */}
                    <div>
                      <h4 className="font-semibold text-gray-900 mb-4">Training Programs:</h4>
                      <div className="flex flex-wrap gap-2 mb-4">
                        {institute.specialties.map((specialty, specIndex) => (
                          <Badge key={specIndex} className={`px-3 py-1 text-xs ${
                            institute.type === 'academic' ? 'bg-red-100 text-red-800 border-red-200' :
                            institute.type === 'certification' ? 'bg-blue-100 text-blue-800' :
                            institute.type === 'founder' ? 'bg-yellow-100 text-yellow-800' :
                            institute.type === 'executive' ? 'bg-purple-100 text-purple-800 border-purple-200' :
                            'bg-green-100 text-green-800'
                          }`}>
                            {specialty}
                          </Badge>
                        ))}
                      </div>
                      
                      <div className="mb-4">
                        <h4 className="font-medium text-gray-700 mb-2">Target Audience:</h4>
                        <p className="text-sm text-gray-600">{institute.targetAudience}</p>
                      </div>

                      {institute.pricing && (
                        <div className="mb-4">
                          <h4 className="font-medium text-gray-700 mb-2">Investment:</h4>
                          <p className="text-sm text-green-600 font-medium">{institute.pricing}</p>
                        </div>
                      )}

                      <div className="mb-4 p-3 bg-gray-50 rounded-lg">
                        <h4 className="font-medium text-gray-700 mb-2">Unique Value:</h4>
                        <p className="text-sm text-gray-600 italic">{institute.uniqueValue}</p>
                      </div>
                      
                      <Button 
                        className={`w-full font-semibold ${
                          institute.type === 'academic' ? 'bg-red-600 hover:bg-red-700' :
                          institute.type === 'certification' ? 'bg-blue-600 hover:bg-blue-700' :
                          institute.type === 'founder' ? 'bg-yellow-600 hover:bg-yellow-700' :
                          institute.type === 'executive' ? 'bg-purple-600 hover:bg-purple-700' :
                          'bg-green-600 hover:bg-green-700'
                        }`}
                        onClick={() => window.open(institute.website, '_blank')}
                      >
                        <Users className="mr-2 h-5 w-5" />
                        Visit {institute.name}
                      </Button>
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          </Card>

          {/* Training ROI */}
          <Card className="p-8 mb-12 bg-gradient-to-r from-yellow-50 to-orange-50 border-yellow-200">
            <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
              <TrendingUp className="mr-3 h-6 w-6 text-yellow-600" />
              Training Investment ROI
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div>
                <h3 className="text-xl font-semibold text-gray-900 mb-4">Why Training Investment Pays</h3>
                <ul className="space-y-3 text-gray-700">
                  <li className="flex items-start">
                    <TrendingUp className="mr-2 h-5 w-5 text-green-500 mt-0.5" />
                    <span><strong>Faster Implementation:</strong> Trained teams implement QRM 3x faster</span>
                  </li>
                  <li className="flex items-start">
                    <TrendingUp className="mr-2 h-5 w-5 text-green-500 mt-0.5" />
                    <span><strong>Higher Success Rate:</strong> 90%+ success vs 30% without proper training</span>
                  </li>
                  <li className="flex items-start">
                    <TrendingUp className="mr-2 h-5 w-5 text-green-500 mt-0.5" />
                    <span><strong>Sustainable Results:</strong> Trained teams maintain improvements long-term</span>
                  </li>
                  <li className="flex items-start">
                    <TrendingUp className="mr-2 h-5 w-5 text-green-500 mt-0.5" />
                    <span><strong>Avoid Costly Mistakes:</strong> Training prevents $100K+ implementation errors</span>
                  </li>
                </ul>
              </div>
              
              <div>
                <h3 className="text-xl font-semibold text-gray-900 mb-4">Training Investment Breakdown</h3>
                <div className="space-y-3">
                  <div className="flex justify-between items-center p-3 bg-white rounded-lg">
                    <span className="text-gray-700">QRM Alpha (per person):</span>
                    <span className="font-bold text-blue-600">€2,000 - €3,500</span>
                  </div>
                  <div className="flex justify-between items-center p-3 bg-white rounded-lg">
                    <span className="text-gray-700">QRM Silver (per person):</span>
                    <span className="font-bold text-purple-600">€4,000 - €6,000</span>
                  </div>
                  <div className="flex justify-between items-center p-3 bg-white rounded-lg">
                    <span className="text-gray-700">QRM Gold (per person):</span>
                    <span className="font-bold text-yellow-600">€6,000 - €8,500</span>
                  </div>
                  <div className="flex justify-between items-center p-3 bg-yellow-100 rounded-lg border border-yellow-300">
                    <span className="font-bold text-gray-900">Typical Team Training:</span>
                    <span className="font-bold text-yellow-700">€25,000 - €75,000</span>
                  </div>
                </div>
              </div>
            </div>
          </Card>

          {/* Implementation Path */}
          <Card className="p-8 mb-12">
            <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
              <ArrowRight className="mr-3 h-6 w-6 text-gray-600" />
              Recommended Learning Path (Jan Pauwels Advisory Approach)
            </h2>
            
            {/* Introduction Step */}
            <div className="bg-yellow-50 p-6 rounded-lg border border-yellow-200 mb-8">
              <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center">
                <Lightbulb className="mr-2 h-5 w-5 text-yellow-600" />
                Start Here: Foundation Reading & Masterclass
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <h4 className="font-semibold text-gray-800 mb-2">📖 Essential Reading</h4>
                  <p className="text-gray-700 text-sm mb-3">
                    Buy and read <strong>"It's About Time"</strong> by Prof. Rajan Suri. 
                    This is the definitive QRM book that creates the foundation for everything else.
                  </p>
                  <Badge className="bg-yellow-100 text-yellow-800">Prerequisite</Badge>
                </div>
                <div>
                  <h4 className="font-semibold text-gray-800 mb-2">🎓 Expert Validation</h4>
                  <p className="text-gray-700 text-sm mb-3">
                    If the book resonates, book a <strong>masterclass with Prof. Suri himself</strong>. 
                    This validates your commitment and provides direct creator guidance.
                  </p>
                  <Badge className="bg-blue-100 text-blue-800">Recommended</Badge>
                </div>
              </div>
            </div>
            
            <div className="relative">
              <div className="absolute left-6 top-0 bottom-0 w-0.5 bg-blue-200"></div>
              
              <div className="space-y-8">
                <div className="flex items-start">
                  <div className="w-12 h-12 bg-blue-500 text-white rounded-full flex items-center justify-center font-bold text-lg mr-6 relative z-10">
                    1
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-gray-900 mb-2">Start with QRM Alpha Training</h3>
                    <p className="text-gray-600 mb-3">Foundation for entire team (plant managers, team leaders, key personnel)</p>
                    <Badge className="bg-blue-100 text-blue-800">Essential First Step</Badge>
                    <div className="mt-3 p-3 bg-blue-50 rounded-lg">
                      <p className="text-sm text-blue-800">
                        <strong>Advisory Integration:</strong> Schedule initial advisory session to assess readiness and plan training approach
                      </p>
                    </div>
                  </div>
                </div>

                <div className="flex items-start">
                  <div className="w-12 h-12 bg-purple-500 text-white rounded-full flex items-center justify-center font-bold text-lg mr-6 relative z-10">
                    2
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-gray-900 mb-2">Calculate Business Case</h3>
                    <p className="text-gray-600 mb-3">Use our EBITDA calculator to justify training and implementation investment</p>
                    <Button 
                      variant="outline" 
                      className="px-4 py-2 border-purple-600 text-purple-600 mb-3"
                      onClick={() => window.location.href = '/business-case'}
                    >
                      Calculate ROI Now
                    </Button>
                    <div className="p-3 bg-purple-50 rounded-lg">
                      <p className="text-sm text-purple-800">
                        <strong>Advisory Support:</strong> Quarterly progress reviews (3-month intervals) to track results, identify obstacles, and adjust strategy
                      </p>
                    </div>
                  </div>
                </div>

                <div className="flex items-start">
                  <div className="w-12 h-12 bg-green-500 text-white rounded-full flex items-center justify-center font-bold text-lg mr-6 relative z-10">
                    3
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-gray-900 mb-2">Advanced Training & Implementation</h3>
                    <p className="text-gray-600 mb-3">QRM Silver for implementation teams, QRM Gold for leaders and experts</p>
                    <Badge className="bg-green-100 text-green-800">Continuous Learning</Badge>
                    <div className="mt-3 p-3 bg-green-50 rounded-lg">
                      <p className="text-sm text-green-800">
                        <strong>3-Year Advisory Journey:</strong> Regular advisory touchpoints ensure sustainable transformation. 
                        Quarterly reviews focus on accountability, continuous improvement, and lasting impact.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            
            {/* Advisory Approach */}
            <div className="mt-8 p-6 bg-gradient-to-r from-indigo-50 to-blue-50 rounded-lg border border-indigo-200">
              <h3 className="text-lg font-bold text-indigo-900 mb-4">🎯 Jan Pauwels Advisory Approach</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <h4 className="font-semibold text-indigo-800 mb-2">Advisory Philosophy:</h4>
                  <ul className="text-sm text-indigo-700 space-y-1">
                    <li>• Regular advisory sessions from the very start</li>
                    <li>• Full journey takes about three years</li>
                    <li>• Quarterly progress conversations (every 3 months)</li>
                    <li>• Focus on results, obstacles, and next steps</li>
                  </ul>
                </div>
                <div>
                  <h4 className="font-semibold text-indigo-800 mb-2">Value Promise:</h4>
                  <ul className="text-sm text-indigo-700 space-y-1">
                    <li>• Continuous improvement and accountability</li>
                    <li>• Clear checkpoints to reflect and adjust</li>
                    <li>• Not about selling extra services</li>
                    <li>• Ensuring real, lasting impact</li>
                  </ul>
                </div>
              </div>
              
              <div className="mt-4 p-3 bg-white rounded-lg border border-indigo-300">
                <p className="text-sm text-indigo-800 italic">
                  <strong>Advisory Contact:</strong> This advisory approach delivered successful transformations. 
                  Contact <strong>info@smartqrmnavigator.com</strong> for guidance on your QRM journey.
                </p>
              </div>
            </div>
          </Card>

          {/* Call to Action */}
          <Card className="p-8 bg-gradient-to-r from-blue-600 to-indigo-600 text-white text-center">
            <h2 className="text-2xl font-bold mb-4">Ready to Start Your QRM Education Journey?</h2>
            <p className="text-blue-100 mb-6 text-lg">
              Begin with professional QRM training to ensure successful implementation and maximize your ROI
            </p>
            <div className="space-x-4">
              <Button 
                size="lg" 
                className="px-8 py-3 bg-white text-blue-600 hover:bg-blue-50 font-semibold"
                onClick={() => window.location.href = '/business-case'}
              >
                <BarChart3 className="mr-2 h-5 w-5" />
                Calculate Training ROI
              </Button>
              <Button 
                variant="outline"
                size="lg" 
                className="px-8 py-3 border-2 border-white text-white hover:bg-white hover:text-blue-600 font-semibold"
                onClick={() => window.location.href = '/qrm-vocabulary'}
              >
                <Lightbulb className="mr-2 h-5 w-5" />
                Learn QRM Terminology
              </Button>
            </div>
          </Card>

          {/* Navigation */}
          <div className="text-center mt-12">
            <div className="space-x-4">
              <Button 
                variant="outline"
                onClick={() => window.location.href = '/success-cases'}
                className="px-8 py-3"
              >
                ← Success Cases
              </Button>
              <Button 
                onClick={() => window.location.href = '/'}
                className="px-8 py-3 bg-blue-600 hover:bg-blue-700"
              >
                Back to Home
              </Button>
            </div>
          </div>

        </div>
      </div>
    </div>
  );
};

export default QRMEducation;
